import { BookitupScope } from './ProfileS';

type ScopeRequirement = readonly BookitupScope[];

const matchesSection = (pathname: string, section: string) =>
  pathname === section || pathname.startsWith(`${section}/`);

/**
 * Returns groups of scopes required by a page. Every group must be satisfied,
 * while a user only needs one of the scopes within a group.
 */
const getPageScopeRequirements = (rawPathname: string): ScopeRequirement[] => {
  const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname;
  const requirements: ScopeRequirement[] = [];

  if (matchesSection(pathname, '/resources')) {
    requirements.push(['RESOURCE']);
  } else if (matchesSection(pathname, '/contacts')) {
    requirements.push(['CONTACTS']);
  } else if (matchesSection(pathname, '/postbox')) {
    requirements.push(['EMAILS']);
  } else if (matchesSection(pathname, '/statistics')) {
    requirements.push(['STATISTICS']);
  } else if (matchesSection(pathname, '/documents')) {
    requirements.push(['DOCUMENTS', 'CONTRACTS', 'QUESTIONNAIRES']);
  }

  if (/^\/contacts\/[^/]+\/emails(?:\/|$)/.test(pathname)) {
    requirements.push(['EMAILS']);
  } else if (/^\/contacts\/[^/]+\/documents(?:\/|$)/.test(pathname)) {
    requirements.push(['DOCUMENTS']);
  }

  if (/^\/events\/[^/]+\/resources(?:\/|$)/.test(pathname)) {
    requirements.push(['RESOURCE']);
  } else if (/^\/events\/[^/]+\/emails(?:\/|$)/.test(pathname)) {
    requirements.push(['EMAILS']);
  } else if (/^\/events\/[^/]+\/contracts(?:\/|$)/.test(pathname)) {
    requirements.push(['CONTRACTS']);
  } else if (/^\/events\/[^/]+\/questionnaires(?:\/|$)/.test(pathname)) {
    requirements.push(['QUESTIONNAIRES']);
  } else if (/^\/events\/[^/]+\/documents\/.+/.test(pathname)) {
    requirements.push(['DOCUMENTS']);
  } else if (/^\/events\/[^/]+\/documents$/.test(pathname)) {
    requirements.push(['DOCUMENTS', 'CONTRACTS', 'QUESTIONNAIRES', 'EVENT_UPLOADS']);
  } else if (/^\/events\/[^/]+\/(?:finance|shares)(?:\/|$)/.test(pathname)) {
    requirements.push(['DOCUMENTS']);
  }

  const settingsWithoutSettingsScope = ['/settings', '/settings/account', '/settings/calendar', '/settings/signature'];
  if (matchesSection(pathname, '/settings') && !settingsWithoutSettingsScope.includes(pathname)) {
    requirements.push(['SETTINGS']);

    if (matchesSection(pathname, '/settings/resources')) {
      requirements.push(['RESOURCE']);
    } else if (
      matchesSection(pathname, '/settings/contracts') ||
      matchesSection(pathname, '/settings/template/contract')
    ) {
      requirements.push(['CONTRACTS']);
    } else if (
      matchesSection(pathname, '/settings/questionnaires') ||
      matchesSection(pathname, '/settings/template/questionnaire')
    ) {
      requirements.push(['QUESTIONNAIRES']);
    } else if (
      [
        '/settings/templates',
        '/settings/positions',
        '/settings/documents',
        '/settings/taxes',
        '/settings/sequentialnumbers',
      ].some((section) => matchesSection(pathname, section)) ||
      matchesSection(pathname, '/settings/template/document')
    ) {
      requirements.push(['DOCUMENTS']);
    }
  }

  return requirements;
};

const hasPagePermission = (pathname: string, scopes: BookitupScope[] = []) =>
  getPageScopeRequirements(pathname).every((requiredScopes) =>
    requiredScopes.some((requiredScope) => scopes.includes(requiredScope)),
  );

// eslint-disable-next-line import/prefer-default-export
export const PagePermissionS = {
  getPageScopeRequirements,
  hasPagePermission,
};
